import React from 'react';
import { useNavigate } from 'react-router-dom';
import { TriageStats } from '../../types';
import { ArrowUpRight, Clock, Target } from 'lucide-react';

interface HomeStatsOverviewProps {
  stats: TriageStats;
}

export const HomeStatsOverview: React.FC<HomeStatsOverviewProps> = ({ stats }) => {
  const navigate = useNavigate();
  const totalFailures = Object.values(stats.failureBreakdown).reduce((a, b) => a + b, 0);

  const mathCount = stats.subjectCounts['MATEMATICA'] || 0;
  const naturezaCount =
    (stats.subjectCounts['FISICA'] || 0) +
    (stats.subjectCounts['QUIMICA'] || 0) +
    (stats.subjectCounts['BIOLOGIA'] || 0);
  const outrasCount =
    (stats.subjectCounts['HUMANAS'] || 0) + (stats.subjectCounts['LINGUAGENS'] || 0);

  const totalBank = 250;
  const progressPct = Math.min(100, Math.round((stats.totalAttempts / totalBank) * 100));

  const mathPct = Math.round((mathCount / totalBank) * 100);
  const natPct = Math.round((naturezaCount / totalBank) * 100);
  const outrasPct = Math.round((outrasCount / totalBank) * 100);

  const isWithinPacingTarget = stats.avgTimeSeconds > 0 && stats.avgTimeSeconds <= 180;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-lg font-semibold text-zinc-900 dark:text-zinc-100">
          Progresso e Desempenho
        </h2>
        <span className="text-xs text-zinc-500 dark:text-zinc-400">
          5 edições oficiais (2024–2026)
        </span>
      </div>

      {/* Asymmetric grid: 1 large progress card + 2 distinct metric cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Large Hero Card: IFF Question Coverage */}
        <div className="md:col-span-2 rounded-xl border border-paper-300 bg-paper-100 p-5 shadow-sm dark:border-study-line dark:bg-study-panel flex flex-col justify-between">
          <div>
            <div className="flex items-baseline justify-between">
              <span className="text-xs font-medium text-zinc-600 dark:text-zinc-400">
                Cobertura do Banco IFF
              </span>
              <span className="font-mono text-xs font-semibold text-zinc-700 dark:text-zinc-300">
                {progressPct}% concluído
              </span>
            </div>

            <div className="mt-2 flex items-baseline gap-2">
              <span className="font-mono text-3xl font-bold tracking-tight text-zinc-900 dark:text-zinc-100">
                {stats.totalAttempts}
              </span>
              <span className="text-sm text-zinc-500 dark:text-zinc-400">
                de {totalBank} questões triadas
              </span>
            </div>

            {/* Segmented Progress Bar */}
            <div className="mt-3.5 h-2.5 w-full rounded-full bg-paper-300 dark:bg-study-card overflow-hidden flex">
              <div
                className="h-full bg-study-amber-500 transition-all duration-500"
                style={{ width: `${mathPct}%` }}
                title={`Matemática: ${mathCount}`}
              />
              <div
                className="h-full bg-study-teal-500 transition-all duration-500"
                style={{ width: `${natPct}%` }}
                title={`Ciências da Natureza: ${naturezaCount}`}
              />
              <div
                className="h-full bg-zinc-400 dark:bg-zinc-600 transition-all duration-500"
                style={{ width: `${outrasPct}%` }}
                title={`Linguagens e Humanas: ${outrasCount}`}
              />
            </div>
          </div>

          <div className="mt-4 pt-3.5 border-t border-paper-200 dark:border-study-line flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
            <div className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-study-amber-500" />
              <span className="text-zinc-700 dark:text-zinc-300">
                Matemática (x5): <strong className="font-mono text-zinc-900 dark:text-zinc-100">{mathCount}</strong>
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-study-teal-500" />
              <span className="text-zinc-700 dark:text-zinc-300">
                Natureza (x2): <strong className="font-mono text-zinc-900 dark:text-zinc-100">{naturezaCount}</strong>
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-zinc-400 dark:bg-zinc-600" />
              <span className="text-zinc-500 dark:text-zinc-400">
                Outras: <strong className="font-mono">{outrasCount}</strong>
              </span>
            </div>
          </div>
        </div>

        {/* Secondary Card: Precisão Geral */}
        <div className="md:col-span-1 rounded-xl border border-paper-300 bg-paper-100 p-5 shadow-sm dark:border-study-line dark:bg-study-panel flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-zinc-600 dark:text-zinc-400">
                Precisão de Triagem
              </span>
              <Target className="h-4 w-4 text-study-teal-600 dark:text-study-teal-400" />
            </div>

            <div className="mt-3">
              <span className="font-mono text-3xl font-bold tracking-tight text-study-teal-700 dark:text-study-teal-400">
                {stats.accuracyRate}%
              </span>
            </div>
          </div>

          <p className="mt-3 text-xs text-zinc-600 dark:text-zinc-400 leading-normal">
            Decodificação assertiva de variáveis e modelo conceitual na 1ª tentativa.
          </p>
        </div>

        {/* Secondary Card: Tempo Médio de Triagem */}
        <div className="md:col-span-1 rounded-xl border border-paper-300 bg-paper-100 p-5 shadow-sm dark:border-study-line dark:bg-study-panel flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-zinc-600 dark:text-zinc-400">
                Cadência Média
              </span>
              <Clock className="h-4 w-4 text-study-amber-600 dark:text-study-amber-400" />
            </div>

            <div className="mt-3 flex items-baseline gap-1.5">
              <span className="font-mono text-3xl font-bold tracking-tight text-zinc-900 dark:text-zinc-100">
                {stats.avgTimeSeconds > 0
                  ? `${Math.floor(stats.avgTimeSeconds / 60)}m ${stats.avgTimeSeconds % 60}s`
                  : '0m 0s'}
              </span>
            </div>
          </div>

          <div className="mt-3 flex items-center gap-1.5 text-xs">
            <span
              className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium ${
                isWithinPacingTarget
                  ? 'bg-study-teal-100 text-study-teal-800 dark:bg-study-teal-950/60 dark:text-study-teal-300'
                  : 'bg-study-amber-100 text-study-amber-800 dark:bg-study-amber-950/60 dark:text-study-amber-300'
              }`}
            >
              {isWithinPacingTarget ? 'Dentro do ritmo' : 'Meta: sob 3 min'}
            </span>
          </div>
        </div>
      </div>

      {/* Caderno de Erros Integrated Diagnostic Bar */}
      {totalFailures > 0 && (
        <div className="rounded-xl border border-paper-300 bg-paper-100 p-5 shadow-sm dark:border-study-line dark:bg-study-panel">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-xs font-semibold text-zinc-900 dark:text-zinc-100">
                Distribuição de Falhas por Causa-Raiz
              </h3>
              <p className="text-[11px] text-zinc-500 dark:text-zinc-400 mt-0.5">
                {totalFailures} questões com registro de atrito no caderno de erros
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center gap-1 text-xs font-medium text-study-teal-700 hover:text-study-teal-800 dark:text-study-teal-400 dark:hover:text-study-teal-300"
            >
              Auditar caderno
              <ArrowUpRight className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Segmented Diagnostic Bar */}
          <div className="h-2.5 w-full rounded-full bg-paper-200 dark:bg-study-card overflow-hidden flex">
            <div
              className="h-full bg-rose-500 transition-all duration-300"
              style={{
                width: `${Math.round((stats.failureBreakdown.OBSCURED_THEORY / totalFailures) * 100)}%`,
              }}
              title="Decodificação"
            />
            <div
              className="h-full bg-study-amber-500 transition-all duration-300"
              style={{
                width: `${Math.round((stats.failureBreakdown.FORGOT_FORMULA / totalFailures) * 100)}%`,
              }}
              title="Memória de fórmula"
            />
            <div
              className="h-full bg-blue-500 transition-all duration-300"
              style={{
                width: `${Math.round((stats.failureBreakdown.CALCULATION_ERROR / totalFailures) * 100)}%`,
              }}
              title="Execução / Cálculo"
            />
            <div
              className="h-full bg-study-teal-500 transition-all duration-300"
              style={{
                width: `${Math.round((stats.failureBreakdown.MISREAD_INPUTS / totalFailures) * 100)}%`,
              }}
              title="Leitura / Inputs"
            />
          </div>

          <div className="mt-3.5 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-rose-500 shrink-0" />
              <div className="min-w-0">
                <span className="text-zinc-600 dark:text-zinc-400 truncate block text-[11px]">
                  Decodificação
                </span>
                <span className="font-mono font-semibold text-zinc-900 dark:text-zinc-100">
                  {Math.round((stats.failureBreakdown.OBSCURED_THEORY / totalFailures) * 100)}%
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-study-amber-500 shrink-0" />
              <div className="min-w-0">
                <span className="text-zinc-600 dark:text-zinc-400 truncate block text-[11px]">
                  Memória de fórmula
                </span>
                <span className="font-mono font-semibold text-zinc-900 dark:text-zinc-100">
                  {Math.round((stats.failureBreakdown.FORGOT_FORMULA / totalFailures) * 100)}%
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-blue-500 shrink-0" />
              <div className="min-w-0">
                <span className="text-zinc-600 dark:text-zinc-400 truncate block text-[11px]">
                  Execução e cálculo
                </span>
                <span className="font-mono font-semibold text-zinc-900 dark:text-zinc-100">
                  {Math.round((stats.failureBreakdown.CALCULATION_ERROR / totalFailures) * 100)}%
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-study-teal-500 shrink-0" />
              <div className="min-w-0">
                <span className="text-zinc-600 dark:text-zinc-400 truncate block text-[11px]">
                  Leitura de dados
                </span>
                <span className="font-mono font-semibold text-zinc-900 dark:text-zinc-100">
                  {Math.round((stats.failureBreakdown.MISREAD_INPUTS / totalFailures) * 100)}%
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
